import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
    en: {
        // Common
        dashboard: 'Dashboard',
        myTickets: 'My Tickets',
        newTicket: 'New Ticket',
        settings: 'Settings',
        logout: 'Logout',
        loading: 'Loading...',
        save: 'Save',
        cancel: 'Cancel',
        submit: 'Submit',
        back: 'Back',
        
        // Dashboard
        welcomeBack: 'Welcome back',
        ticketsOverview: "Here's an overview of your support tickets",
        totalTickets: 'Total Tickets',
        openTickets: 'Open Tickets',
        awaitingResponse: 'Awaiting Response',
        resolved: 'Resolved',
        closed: 'Closed',
        recentTickets: 'Recent Tickets',
        viewAll: 'View All',
        noTicketsYet: 'No tickets yet',
        createFirstTicket: 'Create your first support ticket to get help from our team',
        createTicket: 'Create Ticket',
        quickHelp: 'Quick Help',
        knowledgeBase: 'Knowledge Base',
        browseDocumentation: 'Browse our documentation for answers',
        whatsappSupport: 'WhatsApp Support',
        chatOnWhatsapp: 'Chat with us on WhatsApp',
        contactUs: 'Contact Us',
        reachUs: 'Reach us via phone or email',
        
        // Tickets
        ticketNumber: 'Ticket #',
        subject: 'Subject',
        status: 'Status',
        priority: 'Priority',
        category: 'Category',
        created: 'Created',
        lastUpdated: 'Last Updated',
        conversation: 'Conversation',
        addReply: 'Add Reply',
        sendReply: 'Send Reply',
        attachFiles: 'Attach Files',
        typeMessage: 'Type your message here...',
        closeTicket: 'Close Ticket',
        reopenTicket: 'Reopen Ticket',
        ticketClosed: 'This ticket is closed. Click "Reopen Ticket" above to continue the conversation.',
        ticketClosedSuccess: 'Ticket closed successfully',
        ticketReopenedSuccess: 'Ticket reopened successfully',
        replySentSuccess: 'Reply sent successfully',
        backToTickets: 'Back to Tickets',
        ticketDetails: 'Ticket Details',
        
        // Create Ticket
        createNewTicket: 'Create New Ticket',
        submitRequest: 'Submit a support request and our team will help you',
        description: 'Description',
        descriptionPlaceholder: 'Please provide as much detail as possible about your issue...',
        attachments: 'Attachments',
        dragDrop: 'Click to upload or drag and drop',
        fileTypes: 'JPG, PNG, GIF, PDF, DOC, XLS up to 10MB (max 5 files)',
        submitTicket: 'Submit Ticket',
        ticketCreatedSuccess: 'Ticket created successfully!',
        selectCategory: 'Select a category',
        
        // Statuses
        statusOpen: 'Open',
        statusAssigned: 'Assigned',
        statusInProgress: 'In Progress',
        statusWaiting: 'Awaiting Response',
        statusResolved: 'Resolved',
        statusClosed: 'Closed',
        
        // Priorities
        priorityLow: 'Low',
        priorityMedium: 'Medium',
        priorityHigh: 'High',
        priorityCritical: 'Critical',
        
        // Auth
        signIn: 'Sign In',
        signInToAccount: 'Sign in to your support account',
        emailAddress: 'Email Address',
        password: 'Password',
        forgotPassword: 'Forgot password?',
        noAccount: "Don't have an account?",
        createAccount: 'Create Account',
        register: 'Register',
        createSupportAccount: 'Create your support account',
        fullName: 'Full Name',
        phone: 'Phone Number',
        company: 'Company Name',
        haveAccount: 'Already have an account?',
        confirmPassword: 'Confirm Password',
        
        // Profile
        profileSettings: 'Profile Settings',
        updateProfile: 'Update your personal information',
        changePassword: 'Change Password',
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        profileUpdated: 'Profile updated successfully',
        passwordChanged: 'Password changed successfully',
        
        // Errors
        loginFailed: 'Login failed. Please try again.',
        registrationFailed: 'Registration failed. Please try again.',
        failedToLoad: 'Failed to load data',
        failedToCreate: 'Failed to create ticket',
        failedToClose: 'Failed to close ticket',
        failedToReopen: 'Failed to reopen ticket',
        failedToSendReply: 'Failed to send reply',
        enterMessage: 'Please enter a message',
        requiredFields: 'Please fill in all required fields',
    },
    ar: {
        // Common
        dashboard: 'لوحة التحكم',
        myTickets: 'تذاكري',
        newTicket: 'تذكرة جديدة',
        settings: 'الإعدادات',
        logout: 'تسجيل الخروج',
        loading: 'جاري التحميل...',
        save: 'حفظ',
        cancel: 'إلغاء',
        submit: 'إرسال',
        back: 'رجوع',
        
        // Dashboard
        welcomeBack: 'مرحباً بعودتك',
        ticketsOverview: 'إليك نظرة عامة على تذاكر الدعم الخاصة بك',
        totalTickets: 'إجمالي التذاكر',
        openTickets: 'التذاكر المفتوحة',
        awaitingResponse: 'بانتظار الرد',
        resolved: 'تم الحل',
        closed: 'مغلقة',
        recentTickets: 'التذاكر الأخيرة',
        viewAll: 'عرض الكل',
        noTicketsYet: 'لا توجد تذاكر بعد',
        createFirstTicket: 'أنشئ أول تذكرة دعم للحصول على المساعدة من فريقنا',
        createTicket: 'إنشاء تذكرة',
        quickHelp: 'مساعدة سريعة',
        knowledgeBase: 'قاعدة المعرفة',
        browseDocumentation: 'تصفح الوثائق للحصول على إجابات',
        whatsappSupport: 'دعم واتساب',
        chatOnWhatsapp: 'تواصل معنا عبر واتساب',
        contactUs: 'اتصل بنا',
        reachUs: 'تواصل معنا عبر الهاتف أو البريد الإلكتروني',
        
        // Tickets
        ticketNumber: 'رقم التذكرة',
        subject: 'الموضوع',
        status: 'الحالة',
        priority: 'الأولوية',
        category: 'التصنيف',
        created: 'تاريخ الإنشاء',
        lastUpdated: 'آخر تحديث',
        conversation: 'المحادثة',
        addReply: 'إضافة رد',
        sendReply: 'إرسال الرد',
        attachFiles: 'إرفاق ملفات',
        typeMessage: 'اكتب رسالتك هنا...',
        closeTicket: 'إغلاق التذكرة',
        reopenTicket: 'إعادة فتح التذكرة',
        ticketClosed: 'هذه التذكرة مغلقة. انقر على "إعادة فتح التذكرة" أعلاه لمتابعة المحادثة.',
        ticketClosedSuccess: 'تم إغلاق التذكرة بنجاح',
        ticketReopenedSuccess: 'تم إعادة فتح التذكرة بنجاح',
        replySentSuccess: 'تم إرسال الرد بنجاح',
        backToTickets: 'العودة للتذاكر',
        ticketDetails: 'تفاصيل التذكرة',
        
        // Create Ticket
        createNewTicket: 'إنشاء تذكرة جديدة',
        submitRequest: 'أرسل طلب دعم وسيساعدك فريقنا',
        description: 'الوصف',
        descriptionPlaceholder: 'يرجى تقديم أكبر قدر ممكن من التفاصيل حول مشكلتك...',
        attachments: 'المرفقات',
        dragDrop: 'انقر للرفع أو اسحب وأفلت',
        fileTypes: 'JPG, PNG, GIF, PDF, DOC, XLS حتى 10 ميجابايت (5 ملفات كحد أقصى)',
        submitTicket: 'إرسال التذكرة',
        ticketCreatedSuccess: 'تم إنشاء التذكرة بنجاح!',
        selectCategory: 'اختر تصنيفاً',
        
        // Statuses
        statusOpen: 'مفتوحة',
        statusAssigned: 'تم التعيين',
        statusInProgress: 'قيد التنفيذ',
        statusWaiting: 'بانتظار الرد',
        statusResolved: 'تم الحل',
        statusClosed: 'مغلقة',
        
        // Priorities
        priorityLow: 'منخفضة',
        priorityMedium: 'متوسطة',
        priorityHigh: 'عالية',
        priorityCritical: 'حرجة',
        
        // Auth
        signIn: 'تسجيل الدخول',
        signInToAccount: 'سجل دخولك إلى حساب الدعم',
        emailAddress: 'البريد الإلكتروني',
        password: 'كلمة المرور',
        forgotPassword: 'نسيت كلمة المرور؟',
        noAccount: 'ليس لديك حساب؟',
        createAccount: 'إنشاء حساب',
        register: 'التسجيل',
        createSupportAccount: 'أنشئ حساب الدعم الخاص بك',
        fullName: 'الاسم الكامل',
        phone: 'رقم الهاتف',
        company: 'اسم الشركة',
        haveAccount: 'لديك حساب بالفعل؟',
        confirmPassword: 'تأكيد كلمة المرور',
        
        // Profile
        profileSettings: 'إعدادات الملف الشخصي',
        updateProfile: 'تحديث معلوماتك الشخصية',
        changePassword: 'تغيير كلمة المرور',
        currentPassword: 'كلمة المرور الحالية',
        newPassword: 'كلمة المرور الجديدة',
        profileUpdated: 'تم تحديث الملف الشخصي بنجاح',
        passwordChanged: 'تم تغيير كلمة المرور بنجاح',
        
        // Errors
        loginFailed: 'فشل تسجيل الدخول. يرجى المحاولة مرة أخرى.',
        registrationFailed: 'فشل التسجيل. يرجى المحاولة مرة أخرى.',
        failedToLoad: 'فشل تحميل البيانات',
        failedToCreate: 'فشل إنشاء التذكرة',
        failedToClose: 'فشل إغلاق التذكرة',
        failedToReopen: 'فشل إعادة فتح التذكرة',
        failedToSendReply: 'فشل إرسال الرد',
        enterMessage: 'يرجى إدخال رسالة',
        requiredFields: 'يرجى ملء جميع الحقول المطلوبة',
    }
};

const LanguageContext = createContext(null);

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}

export function LanguageProvider({ children }) {
    const [language, setLanguage] = useState(() => {
        const saved = localStorage.getItem('helpdesk_language');
        return saved || 'ar'; // Default to Arabic
    });

    useEffect(() => {
        localStorage.setItem('helpdesk_language', language);
        document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = language;
    }, [language]);

    const t = (key) => {
        return translations[language]?.[key] || translations['en']?.[key] || key;
    };

    const toggleLanguage = () => {
        setLanguage(prev => prev === 'en' ? 'ar' : 'en');
    };

    const value = {
        language,
        setLanguage,
        toggleLanguage,
        t,
        isRTL: language === 'ar'
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}
